/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import { Card, Button } from "@material-ui/core";
import { TabLayoutConfig } from "./TabLayoutConfig";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { AGENT_CONSOLE_LAYOUT_CONFIG_KEY, BUILT_IN_TABS } from "../../../../utility/constants";
import { getConfigValue, updateConfigValue } from "../../../../sharedServices/configService";
import { getQueuesAsync } from "../../../../sharedServices/AmazonConnectService";
import { v4 as uuid } from "uuid";
import { DraggableList } from "../../../../components/DraggableList/DraggableList";
import { TAB_TYPES, TAB_VARIANTS, DEFAULT_SETTING } from "../../../../utility/constants";
import { PageHeader, ActionButtons } from "../../../../components/Common";

const COMPONENT_NAME = "Admin settingsManager/components/AgentConsoleLayoutSettings";

// initialize a new tab config object
const newTabConfig = () => ({
	id: uuid(),
	displayName: "",
	description: "",
	queues: [],
	sortOrder: -1,
	variant: TAB_VARIANTS.dynamic,
	type: TAB_TYPES.attribute,
	hidden: false,
	config: {},
});

const reorderTabs = (tabs) => {
	return tabs.map((t, i) => ({ ...t, sortOrder: i }));
};

const isTabConfigValid = (tab) => {
	if (!tab.displayName) {
		return false;
	}

	if (tab.variant === TAB_VARIANTS.dynamic) {
		if (tab.type === TAB_TYPES.attribute && !tab.config?.attributePrefix) {
			return false;
		}

		if (tab.type === TAB_TYPES.embed && !tab.config?.url) {
			return false;
		}
	}

	return true;
};

/**
 * Component used to manage agent console layout configuration
 * @param {Object} params
 * - showSnackbar: (message:string, string) => void - function to show toast messages
 */
export const AgentConsoleLayoutSettings = ({ showSnackbar, setTabModified }) => {
	const [editMode, setEditMode] = useState(false);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [layoutConfig, setLayoutConfig] = useState(null);
	const [availableBuiltInTabs, setAvailableBuiltInTabs] = useState({});
	const [queues, setQueues] = useState([]);
	const setLayoutTabsHandler = (updatedTabs) => {
		setLayoutConfig({ ...layoutConfig, tabs: updatedTabs });
		setEditMode(true);
		setTabModified(true);
	};

	const fetchLayoutConfig = React.useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await getConfigValue(AGENT_CONSOLE_LAYOUT_CONFIG_KEY);
			const newLayoutConfig = response[AGENT_CONSOLE_LAYOUT_CONFIG_KEY]
				? response[AGENT_CONSOLE_LAYOUT_CONFIG_KEY]
				: { tabs: DEFAULT_SETTING.tabs.map((t) => ({ ...BUILT_IN_TABS[t] })) };
			const result = await getQueuesAsync();
			result?.sort((a, b) => (a.Name?.toUpperCase() > b.Name?.toUpperCase() ? 1 : -1));
			setQueues(result);
			let newAvailableBuiltInTabs = { ...BUILT_IN_TABS };
			newLayoutConfig.tabs.forEach((tab) => {
				if (newAvailableBuiltInTabs[tab.id]) delete newAvailableBuiltInTabs[tab.id];
			});
			setLayoutConfig(newLayoutConfig);
			setAvailableBuiltInTabs(newAvailableBuiltInTabs);
		} catch (error) {
			v2.error(`Error while fetching agent console layout configuration`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.FETCH_AGENT_CONSOLE_LAYOUT_SETTINGS_ERROR,
			});
			setLayoutConfig(null);
			showSnackbar("Failed to load layout configuration.", "error");
		} finally {
			setIsLoading(false);
		}
	}, [showSnackbar]);

	const cancelHandler = async () => {
		await fetchLayoutConfig();
		setError("");
		setEditMode(false);
		setTabModified(false);
	};

	const updateHandler = async () => {
		try {
			if (layoutConfig.tabs.some((t) => !isTabConfigValid(t))) {
				setError("Please resolve validation errors before saving");
				return;
			}

			setIsLoading(true);
			await updateConfigValue(AGENT_CONSOLE_LAYOUT_CONFIG_KEY, layoutConfig);
			setError("");
			setEditMode(false);
			setTabModified(false);

			showSnackbar("Successfully saved layout configuration.", "success");
		} catch (error) {
			v2.error(`Error while saving agent console layout config`, {
				additionalData: { error: errorToObject(error), data: { layoutConfig } },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.SAVE_AGENT_CONSOLE_LAYOUT_SETTINGS_ERROR,
			});
			setError("Error occurred while saving, please try again.");
			showSnackbar("Failed to save layout configuration.", "error");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchLayoutConfig();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Card style={{ padding: "25px", flex: 1 }}>
			<div>
				<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
					<PageHeader
						pageTitle="Layout Configuration"
						pageDescription="Optionally enable a tab layout and define tabs to display custom data, like contact flow attributes or
					external applications."
					/>
					{editMode && (
						<ActionButtons showCancel showSave onCancel={cancelHandler} onSave={updateHandler} disableSave={!!error} />
					)}
				</div>
				<div style={{ position: "relative", minHeight: 300, display: "flex", flexDirection: "column" }}>
					{isLoading ? (
						<i
							style={{ left: "50%", position: "absolute", top: "50%" }}
							className="fas fa-spinner loading-spinner spin-animation"
						/>
					) : !layoutConfig ? (
						<div className="mt-4">
							<span className="text-danger mr-2">Failed to load layout configuration, please try again.</span>
						</div>
					) : (
						<>
							<div className="mt-4 d-flex justify-content-end">
								<Button
									variant="outlined"
									size="small"
									onClick={() => setLayoutTabsHandler(reorderTabs([{ ...newTabConfig() }, ...layoutConfig.tabs]))}
								>
									Add new tab
								</Button>
							</div>
							{!layoutConfig.tabs.length ? (
								<div style={{ margin: "200px auto", color: "gray" }}>The are currently no tabs.</div>
							) : (
								<div style={{ flex: 1 }} className="mt-2">
									<div className="mb-5">
										<DraggableList
											items={layoutConfig.tabs}
											setItems={(newTabOrder) => {
												setLayoutTabsHandler(reorderTabs(newTabOrder));
											}}
											renderItem={(tab, index) => (
												<TabLayoutConfig
													queues={queues}
													tab={tab}
													formEdited={editMode}
													setFormEdited={setEditMode}
													availableBuiltInTabs={availableBuiltInTabs}
													removeTabConfig={() => {
														let newTabs = [...layoutConfig.tabs];
														newTabs.splice(index, 1);
														setLayoutTabsHandler(newTabs);
														if (BUILT_IN_TABS[tab.id]) {
															setAvailableBuiltInTabs({
																...availableBuiltInTabs,
																[tab.id]: { ...BUILT_IN_TABS[tab.id] },
															});
														}
													}}
													setTabConfig={(updatedTab) => {
														const copy = layoutConfig.tabs.slice();
														copy[index] = updatedTab;
														setLayoutTabsHandler(copy);
														if (tab.id !== updatedTab.id) {
															const newAvailableBuiltInTabs = { ...availableBuiltInTabs };
															if (availableBuiltInTabs[updatedTab.id]) {
																delete newAvailableBuiltInTabs[updatedTab.id];
															}
															if (BUILT_IN_TABS[tab.id]) {
																newAvailableBuiltInTabs[tab.id] = { ...BUILT_IN_TABS[tab.id] };
															}
															setAvailableBuiltInTabs(newAvailableBuiltInTabs);
														}
													}}
												/>
											)}
										/>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</Card>
	);
};
